// src/pages/BlogPage.jsx – Blog index: design aligned with FAQ/About (hero, geometric background, professional cards)
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, FileText, Newspaper } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { BLOG_POSTS } from '../data/blogPosts';

const MOBILE_BREAKPOINT = 768;

// Newest first (stable for posts with the same date). The grid shows a page of cards
// and a "Show more" button; the remaining cards are still rendered (with the `hidden`
// attribute), so every post link stays in the prerendered HTML for crawlers.
const POSTS = [...BLOG_POSTS].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
const PAGE_SIZE = 6;
// The newest post is the featured card in the hero, so the grid starts with the next one.
const GRID_POSTS = POSTS.slice(1);

const BLOG_INDEX_TITLE = 'Blog | Logami – TELC, Goethe & ÖSD Exam Tips';
const BLOG_INDEX_DESCRIPTION =
  'Expert guides and Redemittel for TELC, Goethe, and ÖSD German exams. Free PDFs, exam strategies, and practice tips to pass B1–C1 on your first try.';

const BlogPage = () => {
  const { lang } = useParams();
  const { t } = useTranslation();
  const basePath = lang ? `/${lang}/blog` : '/blog';
  const [heroRef, heroVisible] = useScrollAnimation();
  const [gridRef, gridVisible] = useScrollAnimation();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // SEO: title, meta description, OG + Twitter tags for blog index (canonical set by App.jsx)
  useEffect(() => {
    const baseUrl = 'https://logami.app';
    const canonicalUrl = `${baseUrl}/${lang || 'en'}/blog`;
    const ogImage = `${baseUrl}/logo-icon.png?v=5`;

    const prevTitle = document.title;
    document.title = BLOG_INDEX_TITLE;

    let metaDesc = document.querySelector('meta[name="description"]');
    const createdDesc = !metaDesc;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    const prevContent = metaDesc.getAttribute('content') || '';
    metaDesc.setAttribute('content', BLOG_INDEX_DESCRIPTION);

    const setMeta = (attr, key, value) => {
      const el = document.querySelector(attr === 'property' ? `meta[property="${key}"]` : `meta[name="${key}"]`);
      if (el) el.setAttribute('content', value);
      else {
        const m = document.createElement('meta');
        if (attr === 'property') m.setAttribute('property', key);
        else m.setAttribute('name', key);
        m.setAttribute('content', value);
        document.head.appendChild(m);
      }
    };

    const ogTags = [
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: BLOG_INDEX_TITLE },
      { property: 'og:description', content: BLOG_INDEX_DESCRIPTION },
      { property: 'og:image', content: ogImage },
      { property: 'og:url', content: canonicalUrl },
    ];
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: BLOG_INDEX_TITLE },
      { name: 'twitter:description', content: BLOG_INDEX_DESCRIPTION },
      { name: 'twitter:image', content: ogImage },
    ];

    ogTags.forEach(({ property, content }) => setMeta('property', property, content));
    twitterTags.forEach(({ name, content }) => setMeta('name', name, content));

    const defaultOg = {
      'og:type': 'website',
      'og:title': 'Logami – Pass TELC, Goethe & ÖSD German Exams',
      'og:description': 'Logami is the only B1–C1 platform built by actual German teachers. Stop relying on outdated PDFs! Practice with authentic TELC, Goethe, and ÖSD mock exams.',
      'og:image': ogImage,
      'og:url': `${baseUrl}/`,
    };
    const defaultTwitter = {
      'twitter:card': 'summary_large_image',
      'twitter:title': defaultOg['og:title'],
      'twitter:description': defaultOg['og:description'],
      'twitter:image': ogImage,
    };

    return () => {
      document.title = prevTitle;
      if (createdDesc && metaDesc?.parentNode) metaDesc.remove();
      else if (metaDesc) metaDesc.setAttribute('content', prevContent);
      Object.entries(defaultOg).forEach(([k, v]) => setMeta('property', k, v));
      Object.entries(defaultTwitter).forEach(([k, v]) => setMeta('name', k, v));
    };
  }, [lang]);

  // On real mobile devices (iPhone/Android), IntersectionObserver can fail to fire for
  // below-the-fold content (dynamic viewport, PWA, address bar). Always show sections on mobile
  // so all blog posts are visible without relying on scroll-triggered visibility.
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handle = () => setIsNarrowViewport(mq.matches);
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero – compact; title area utilized with featured post on desktop */}
      <section
        ref={heroRef}
        className={`
          relative py-10 sm:py-12 lg:py-14 bg-white overflow-hidden
          transition-all duration-700
          ${heroVisible || isNarrowViewport ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}
        `}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-10 xl:gap-12 items-center">
            {/* Title block – left on desktop, centered on mobile */}
            <div className="lg:col-span-5 text-center lg:text-left">
              <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-black text-white text-xs font-bold tracking-widest uppercase rounded-full mb-3 sm:mb-4">
                <Newspaper className="w-4 h-4 shrink-0" />
                <span>{t('blog.hero.badge')}</span>
              </div>
              <h1 className="font-space text-4xl sm:text-5xl md:text-5xl lg:text-5xl xl:text-6xl font-black tracking-tight text-black mb-3 sm:mb-4 leading-tight">
                {t('blog.hero.heading')}
              </h1>
              <p className="text-base sm:text-lg text-black/70 leading-relaxed max-w-xl lg:max-w-none mx-auto lg:mx-0">
                {t('blog.hero.subtitle')}
              </p>
            </div>

            {/* Featured / latest post – uses the space beside the title on desktop */}
            {POSTS.length > 0 && (
              <div className="lg:col-span-7 mt-8 lg:mt-0">
                <Link
                  to={`${basePath}/${POSTS[0].slug}`}
                  className="group flex flex-col sm:flex-row gap-4 sm:gap-5 p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-white hover:border-black/25 hover:shadow-xl transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
                  aria-label={`Featured: ${POSTS[0].title}`}
                >
                  <div className="sm:w-72 flex-shrink-0 aspect-[16/10] sm:aspect-video rounded-xl overflow-hidden bg-black/5">
                    <img
                      src={POSTS[0].image}
                      alt={POSTS[0].title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <span className="text-xs font-bold tracking-wider text-black/60 uppercase mb-1">{t('blog.featured.label')}</span>
                    <h2 className="font-space text-lg sm:text-xl font-bold text-black line-clamp-2 group-hover:text-black/90 mb-1">
                      {POSTS[0].title}
                    </h2>
                    <p className="text-black/60 text-sm sm:text-base line-clamp-2 mb-2 leading-relaxed">
                      {POSTS[0].excerpt}
                    </p>
                    <span className="inline-flex items-center font-bold text-black text-sm group-hover:translate-x-0.5 transition-transform">
                      {t('blog.card.read')}
                      <ChevronRight className="w-4 h-4 ml-0.5" />
                    </span>
                  </div>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Blog posts grid – gradient + geometric background like FAQ content section */}
      <section
        ref={gridRef}
        className={`
          relative py-16 bg-gradient-to-br from-gray-50 via-white to-gray-100
          transition-all duration-700 delay-200 overflow-hidden
          ${gridVisible || isNarrowViewport ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}
        `}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {POSTS.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border-2 border-black/10 shadow-xl">
              <p className="text-black/60 font-medium">{t('blog.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {GRID_POSTS.map((post, index) => (
                <Link
                  key={post.slug}
                  hidden={index >= visibleCount}
                  to={`${basePath}/${post.slug}`}
                  className="group relative bg-white rounded-2xl border-2 border-black/10 overflow-hidden transition-all duration-500 hover:border-black/30 hover:shadow-2xl hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
                  aria-label={`Read: ${post.title}`}
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-br from-black/[0.02] via-transparent to-transparent" />
                  </div>
                  <div className="relative aspect-[16/10] overflow-hidden bg-black/5">
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="relative p-5 sm:p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold tracking-wider text-black uppercase">{post.category}</span>
                      {post.pdfUrl && (
                        <span className="inline-flex items-center gap-1 text-xs text-black/50">
                          <FileText className="w-3.5 h-3.5" />
                          PDF
                        </span>
                      )}
                    </div>
                    <h2 className="font-space text-lg sm:text-xl font-bold text-black mb-2 line-clamp-2 group-hover:text-black/90 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-black/60 text-sm sm:text-base line-clamp-3 mb-4 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-black/50">
                      <span>{post.dateFormatted}</span>
                      <span className="inline-flex items-center font-bold text-black group-hover:translate-x-1 transition-transform">
                        {t('blog.card.read')}
                        <ChevronRight className="w-4 h-4 ml-0.5" />
                      </span>
                    </div>
                  </div>
                  {/* Corner accent on hover (like FAQ cards) */}
                  <div className="absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-black/10 rounded-tr-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </Link>
              ))}
            </div>
          )}

          {visibleCount < GRID_POSTS.length && (
            <div className="mt-10 flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                className="inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-black bg-black px-6 py-3 font-space font-bold text-white shadow-md transition-all duration-300 hover:scale-[1.02] hover:bg-black/90 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
              >
                {t('blog.showMore')}
                <ChevronDown className="h-5 w-5" aria-hidden="true" />
              </button>
              <p className="text-sm text-black/50" aria-live="polite">
                {t('blog.showingCount', { shown: Math.min(visibleCount, GRID_POSTS.length) + 1, total: POSTS.length })}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
